<script setup lang="ts">
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import {useExtensionStore} from "@/stores/extension.store";
import {useRoute, useRouter} from "vue-router";
import {computed, onMounted} from "vue";
import SubscriptionPlanExtensionGrid from "@/home/components/SubscriptionPlanExtensionGrid.vue";
import {useSubscriptionPlanStore} from "@/stores/subscription-plan.store";
import {Extension, ExtensionType} from "@/core/interfaces/extension/Extension";
import ProgramExtensionGrid from "@/home/components/ProgramExtensionGrid.vue";
import {storeToRefs} from "pinia";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {ExtensionGrid} from "@/extension/components/extension-grid";
import ExtensionStoreHeader from "@/extension/components/extension-store/ExtensionStoreHeader.vue";
import {EXTENSION_DETAILS_QUERY_KEY} from "@/extension/extensionRouter";

const extensionStore = useExtensionStore();
const {extensions} = storeToRefs(extensionStore);
const {subscriptionPlans} = useSubscriptionPlanStore();
const router = useRouter();
const route = useRoute();

onMounted(() => {
    extensionStore.refreshExtensions();
});

/**
 * The partner extensions.
 */
const partnerExtensions = computed(() => {
    return extensions.value?.filter(extension => {
        return extension.is_partner;
    }) ?? [];
});

/**
 * The loyalty card extensions.
 */
const loyaltyCardExtensions = computed(() => {
    return extensions.value?.filter(extension => {
        return extension.extendable_type === ExtensionType.PROGRAM_BUNDLE && extension.sub_type === 'loyalty-card';
    }) ?? [];
});

/**
 * The hospital extensions.
 */
const hospitalExtensions = computed(() => {
    return extensions.value?.filter((extension) => {
        return extension.organization.entity_type === 'hospital';
    }) ?? [];
});

/**
 * Get the program extensions without a subscription plan and not displayed in other sections.
 */
const orphanProgramExtensions = computed(() => {
    return extensionStore.programExtensions.filter(extension => {
        const extensionNotToDisplays = [
            ...partnerExtensions.value,
            ...loyaltyCardExtensions.value,
            ...hospitalExtensions.value
        ].map(extension => extension.id);

        return !extension.subscription_plan && !extensionNotToDisplays.includes(extension.id);
    }) ?? [];
});

/**
 * Navigate to the extension details.
 */
const navigateToExtensionDetails = (extension: Extension) => {
    router.replace({
        query: {
            ...route.query,
            [EXTENSION_DETAILS_QUERY_KEY]: extension.id
        }
    });
};
</script>

<template>
  <BaseContainer class="space-y-3">
    <ExtensionStoreHeader />
    <div class="p-6">
      <div v-if="extensionStore.loading">
        <BaseFlexSpinner />
      </div>
      <div v-else class="space-y-4">
        <div v-for="plan in subscriptionPlans" :key="plan.id">
          <SubscriptionPlanExtensionGrid
            :plan="plan"
            :enabled="false"
            :appends-icon="false"
            :enable-toggle="true"
            @extension-click="(extension) => navigateToExtensionDetails(extension)"
          />
        </div>
        <ExtensionGrid
          title="Carte de fidélité"
          :extensions="loyaltyCardExtensions"
          @extension-click="(extension) => navigateToExtensionDetails(extension)"
        />
        <ExtensionGrid
          title="Lien ville-hôpital"
          :extensions="hospitalExtensions"
          :enable-toggle="true"
          @extension-click="(extension) => navigateToExtensionDetails(extension)"
        />
        <ExtensionGrid
          title="Partenaires"
          :extensions="partnerExtensions"
          :enable-toggle="true"
          @extension-click="(extension) => navigateToExtensionDetails(extension)"
        />
        <ProgramExtensionGrid
          :appends-icon="false"
          :program-extensions="orphanProgramExtensions"
          :enable-toggle="true"
          @extension-click="(extension) => navigateToExtensionDetails(extension)"
        />
      </div>
    </div>
  </BaseContainer>
</template>

<style scoped>

</style>
